
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useCreateClass } from '@/api/classes';
import { StaffSelector } from './staff-selector';
import { DaysOfWeekSelector } from './days-of-week-selector';

const classSchema = z.object({
  name: z.string().min(1, 'Class name is required'),
  label: z.string().min(1, 'Class label is required'),
  staffMember: z.string().optional(),
  daysOfWeek: z.array(z.number()).optional(),
});

type ClassFormData = z.infer<typeof classSchema>;

interface AddClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddClassDialog({ open, onOpenChange }: AddClassDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const createClassMutation = useCreateClass();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ClassFormData>({
    resolver: zodResolver(classSchema),
    defaultValues: {
      name: '',
      label: '',
      staffMember: '',
      daysOfWeek: [],
    },
  });

  const staffMember = watch('staffMember');
  const daysOfWeek = watch('daysOfWeek');

  const onSubmit = async (data: ClassFormData) => {
    try {
      const payload: any = {
        name: data.name,
        label: data.label,
      };
      
      if (data.staffMember) {
        payload.staffMember = data.staffMember;
      }
      
      if (data.daysOfWeek && data.daysOfWeek.length > 0) {
        payload.daysOfWeek = data.daysOfWeek;
      }
      
      await createClassMutation.mutateAsync(payload);
      toast({
        title: t('common.success'),
        description: t('classes.classCreated'),
      });
      reset();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.message || t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('classes.createClass')}</DialogTitle>
          <DialogDescription>{t('classes.createClass')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('classes.className')}</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder={t('classes.className')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="label">{t('classes.classLabel')}</Label>
            <Input
              id="label"
              {...register('label')}
              placeholder={t('classes.classLabel')}
            />
            {errors.label && (
              <p className="text-sm text-destructive">{errors.label.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="staffMember">
              {t('classes.defaultInstructor', 'Default Instructor')} ({t('common.optional', 'Optional')})
            </Label>
            <StaffSelector
              value={staffMember || ''}
              onChange={(value) => setValue('staffMember', value)}
              allowClear
              placeholder={t('classes.selectInstructor', 'Select instructor...')}
            />
            <p className="text-sm text-muted-foreground">
              {t('classes.defaultInstructorHint', 'This instructor will be assigned to all timeslots by default')}
            </p>
          </div>

          <div className="space-y-2">
            <Label>
              {t('classes.classSchedule', 'Class Schedule')} ({t('common.optional', 'Optional')})
            </Label>
            <DaysOfWeekSelector
              value={daysOfWeek || []}
              onChange={(days) => setValue('daysOfWeek', days)}
            />
            <p className="text-sm text-muted-foreground">
              {t('classes.classScheduleHint', 'If empty, uses timeslot-level settings')}
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={createClassMutation.isPending}>
              {createClassMutation.isPending
                ? t('common.saving')
                : t('common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
